/**
 * My Consent — dashboard of every consent request routed to this device.
 * BRD §7.4: consumer approves, declines, revokes, sees purpose + access log.
 *
 * Statuses: pending | granted | declined | revoked | expired
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  ShieldCheck, Loader2, Building2, Clock, CheckCircle2, XCircle,
  RefreshCw, Ban, KeyRound, ChevronRight,
} from "lucide-react";
import { listConsent, PURPOSE_LABELS } from "@/lib/consent";
import { emitSignal } from "@/lib/wallet-signal";

const STATUS_TONE = {
  pending:  { color: "#B45309", bg: "#FEF3C7", label: "Awaiting your decision", Icon: Clock },
  granted:  { color: "#065F46", bg: "#D1FAE5", label: "Granted", Icon: CheckCircle2 },
  declined: { color: "#7F1D1D", bg: "#FEE2E2", label: "Declined", Icon: XCircle },
  revoked:  { color: "#475569", bg: "#E2E8F0", label: "Revoked", Icon: Ban },
  expired:  { color: "#475569", bg: "#E2E8F0", label: "Expired", Icon: Ban },
};

export default function WalletConsent() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    emitSignal("wallet.consent.list_opened");
    listConsent()
      .then(setItems)
      .catch((e) => {
        setError(e?.response?.data?.detail || "Load failed");
        setItems([]);
      });
  }, []);

  const grouped = groupByStatus(items || []);

  return (
    <WalletShell activeTab="settings">
      <div className="max-w-4xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-6" data-testid="consent-list-page">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Digital Trust Wallet</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>My Consent</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Every institution that wants to see your data has to ask you first. Grant, decline, or revoke at any time &mdash; and see exactly what they&apos;ve looked at.
          </p>
        </header>

        {items === null && (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="consent-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <div data-testid="consent-error" className="rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800 p-3">
            {error}
          </div>
        )}

        {items && items.length === 0 && (
          <div data-testid="consent-empty" className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
            <ShieldCheck className="w-8 h-8 mx-auto text-slate-300" />
            <div className="mt-3 font-display text-lg font-semibold" style={{ color: NAVY }}>Nothing to review yet</div>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
              When a bank, insurer, or gaming operator wants your data, their request will appear here for your review.
            </p>
          </div>
        )}

        {grouped.pending.length > 0 && (
          <Section title="Awaiting your decision" items={grouped.pending} highlight />
        )}
        {grouped.active.length > 0 && (
          <Section title="Currently granted" items={grouped.active} />
        )}
        {grouped.history.length > 0 && (
          <Section title="History" items={grouped.history} muted />
        )}
      </div>
    </WalletShell>
  );
}

function groupByStatus(items) {
  const pending = items.filter((i) => i.status === "pending");
  const active = items.filter((i) => i.status === "granted");
  const history = items.filter((i) => !["pending", "granted"].includes(i.status));
  return { pending, active, history };
}

function Section({ title, items, highlight, muted }) {
  return (
    <section className="space-y-3">
      <h2
        className="text-[11px] uppercase tracking-widest font-semibold"
        style={{ color: highlight ? "#B45309" : muted ? "#64748B" : NAVY }}
      >
        {title} · {items.length}
      </h2>
      <div className="space-y-3">
        {items.map((c) => (
          <ConsentRow key={c.id} c={c} />
        ))}
      </div>
    </section>
  );
}

function ConsentRow({ c }) {
  const tone = STATUS_TONE[c.status] || STATUS_TONE.pending;
  const purposeLabel = PURPOSE_LABELS[c.purpose] || c.purpose;
  const StatusIcon = tone.Icon;
  return (
    <Link
      to={`/wallet/consent/${c.id}`}
      data-testid={`consent-row-${c.id}`}
      className="block rounded-2xl bg-white border border-slate-100 shadow-sm p-4 lg:p-5 hover:shadow-md transition"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${GOLD}22`, color: NAVY }}
        >
          <KeyRound className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold" style={{ color: NAVY }}>{purposeLabel}</span>
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded font-semibold"
              style={{ background: tone.bg, color: tone.color }}
            >
              <StatusIcon className="w-3 h-3" />
              {tone.label}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500 flex-wrap">
            <Building2 className="w-3 h-3" />
            <span>{c.tenant_name}</span>
            <span>·</span>
            <span>{c.data_categories.length} data categor{c.data_categories.length === 1 ? "y" : "ies"}</span>
            {c.access_count > 0 && (
              <>
                <span>·</span>
                <span data-testid={`consent-access-count-${c.id}`}>
                  Read {c.access_count} time{c.access_count === 1 ? "" : "s"}
                </span>
              </>
            )}
          </div>
          {c.purpose_description && (
            <div className="mt-2 text-[12px] text-slate-700 line-clamp-2">{c.purpose_description}</div>
          )}
          <div className="mt-2 text-[10px] text-slate-500">
            {c.status === "pending" && <>Requested {new Date(c.requested_at).toLocaleDateString()} · Expires {new Date(c.expires_at).toLocaleDateString()}</>}
            {c.status === "granted" && <>Granted {c.decided_at ? new Date(c.decided_at).toLocaleDateString() : "—"} · Expires {new Date(c.expires_at).toLocaleDateString()}</>}
            {c.status === "declined" && <>Declined {c.decided_at ? new Date(c.decided_at).toLocaleDateString() : "—"}</>}
            {c.status === "revoked" && <>Revoked {c.revoked_at ? new Date(c.revoked_at).toLocaleDateString() : "—"}</>}
            {c.status === "expired" && <>Expired {new Date(c.expires_at).toLocaleDateString()}</>}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-2" />
      </div>
    </Link>
  );
}
